import { acceptanceCacheSuffix } from './acceptanceCacheIdentity';
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey } from './types';
import {
  abortableDelay,
  raceWithAbort,
  rethrowIfAborted,
  throwIfAborted,
} from '../../lib/abortSignal';

/**
 * Shape of a cached provider result document in `cache/{cacheKey}`.
 * Shared by the client (`firebase/firestore`) and admin
 * (`firebase-admin/firestore`) write paths so both branches emit the
 * identical document shape and the reader round-trips either one.
 */
interface CacheDocPayload {
  data: unknown;
  sources: unknown;
  cachedAt: string;
  expiresAt: string;
}

/**
 * TASK-57 — server/job context detection.
 *
 * `cache.ts` is imported by BOTH the browser report editor and the
 * server-side SED-bootstrap / report-generation Cloud Run JOBS. In a
 * job there is NO authenticated Firebase user, so the client SDK write
 * (which runs as an unauthenticated principal) is rejected by Firestore
 * security rules with `PERMISSION_DENIED` — noisy logs + every provider
 * re-fetches every run because nothing ever lands in the cache.
 *
 * In the browser `window` is defined; in Node (job/test/server) it is
 * not. This mirrors the existing client-vs-server branch in
 * `costEmergency.ts` (and `probeAvailability.ts`). When server-side we
 * route cache reads/writes through the ADMIN SDK, which bypasses rules.
 */
const isServerContext = (): boolean => typeof window === 'undefined';

/**
 * Resolve the admin Firestore handle for the configured named database
 * (`datacrate-prod`). Mirrors `costEmergency.ts`:
 *   - dynamic-imports `firebase-admin/*` ONLY here so the browser bundle
 *     never pulls `firebase-admin` (this is only reachable behind the
 *     `isServerContext()` guard);
 *   - reuses the already-initialized default app the SED/report jobs set
 *     up via `applicationDefault()`, only initializing if none exists
 *     (e.g. a server path that runs before the job entrypoint inits);
 *   - resolves the DB id from the same source the jobs use.
 *
 * Imported lazily inside the server branch so neither the import nor the
 * `firebase-admin` types leak into the browser path.
 */
async function getAdminFirestore() {
  const { initializeApp, getApps, applicationDefault } = await import('firebase-admin/app');
  const { getFirestore } = await import('firebase-admin/firestore');
  const firebaseConfig = (await import('../../../firebase-applet-config.json')).default;
  if (getApps().length === 0) {
    initializeApp({
      credential: applicationDefault(),
      projectId: firebaseConfig.projectId,
    });
  }
  const dbId =
    process.env.FIRESTORE_DATABASE ?? firebaseConfig.firestoreDatabaseId ?? 'datacrate-prod';
  return getFirestore(dbId);
}

/**
 * Recursively elide `undefined` values from an object before writing
 * to Firestore. `setDoc()` rejects undefined with
 * `"Unsupported field value: undefined"` — but the same payload with
 * the keys omitted writes fine and round-trips identically through
 * the cache reader. Walks arrays too. Leaves `null`, `''`, `0`, and
 * `false` alone (those are legitimate values).
 *
 * Pure / non-mutating — returns a new object/array per branch.
 */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((v) => v !== undefined)
      .map((v) => stripUndefined(v)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

/**
 * PU5 — auto-retry transient provider failures.
 *
 * Bounded backoff: max 2 retries on top of the initial attempt, fixed
 * delays of 1s then 3s. No exponential compounding. Only retries on
 * failures classified as `transient-failure` by `classifyProviderFailure`
 * — `misconfigured` / `permanent-unavailable` / `skipped` errors are
 * thrown immediately so the caller writes the right `failureMode` into
 * `providerStatuses` and the renderer can route to the misconfigured
 * banner instead of silently retrying a broken integration.
 *
 * Why fixed delays + 2 retries (not 3+ exponential):
 *   - The orchestrator runs ALL providers in parallel via
 *     `Promise.allSettled`. The slowest one drives total report
 *     compile time. A long retry tail (e.g. 1+2+4+8s) on every
 *     transient blip would 10-20× the worst-case compile time.
 *   - Real transient blips (CDN routing flips, brief DNS hiccups,
 *     ECONNRESET) typically resolve within 1-3 seconds; if 4s of
 *     wall-clock retrying doesn't recover, the upstream is in a
 *     longer outage and the right action is to surface that to the
 *     user, not to keep retrying.
 *   - Per-call wall-clock is bounded: 15s default fetch timeout ×
 *     3 attempts + 1s + 3s sleep = 49s worst case. Still inside the
 *     45-second Cloud Run default and well clear of the orchestrator
 *     timeout.
 *
 * The retry happens BEFORE the cache write, so a successful retry
 * still populates the cache for the next call — no behaviour change
 * to the cache layer.
 */
const RETRY_DELAYS_MS = [0, 1_000, 3_000] as const;

/**
 * Wrap a provider.fetch call with bounded transient-failure retry.
 *
 * Returns whatever `provider.fetch` returns on success. Throws the
 * LAST error on exhausted retries (or the FIRST permanent error if
 * classification short-circuits the retry loop).
 *
 * Exported so the orchestrator's dispatcher AND any future
 * single-provider rerun path can both use the same retry layer
 * uniformly. `cache.ts` consumes it from `withCache`.
 */
export async function callProviderWithRetry<T>(
  provider: DataProvider<T>,
  lat: number,
  lng: number,
  apiKey?: string,
  geo?: ProviderGeoContext,
): Promise<ProviderResult<T>> {
  throwIfAborted(geo?.signal);
  // Lazy import to avoid the circular dep (index.ts → cache.ts → index.ts).
  // classifyProviderFailure is pure, so the late import is safe.
  const { classifyProviderFailure } = await import('./index');

  let lastError: unknown = new Error(`${provider.id}: no attempts made`);
  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
    throwIfAborted(geo?.signal);
    const delayMs = RETRY_DELAYS_MS[attempt];
    if (delayMs > 0) {
      // eslint-disable-next-line no-console
      console.info(`[provider:${provider.id}] retry`, { attempt, delayMs });
      await abortableDelay(delayMs, geo?.signal);
    }
    try {
      const result = await raceWithAbort(provider.fetch(lat, lng, apiKey, geo), geo?.signal);
      throwIfAborted(geo?.signal);
      return result;
    } catch (e) {
      rethrowIfAborted(geo?.signal, e);
      lastError = e;
      const message = e instanceof Error ? e.message : String(e);
      const failureMode = classifyProviderFailure(provider.id, message);
      // Don't retry permanent failures — that's wasted budget AND
      // suppresses the admin-banner signal for misconfigured.
      if (failureMode !== 'transient-failure') {
        // eslint-disable-next-line no-console
        console.info(`[provider:${provider.id}] retry-abort`, { failureMode, attempt, message });
        throw e;
      }
      // else fall through to the next backoff
    }
  }
  // Retries exhausted — surface the last error so the dispatcher
  // records it with `failureMode: 'transient-failure'`.
  throw lastError;
}

/**
 * Firestore-backed cache for data providers.
 * Stores results by provider ID + grid cell key.
 * Falls through to live API call on cache miss or expiry.
 *
 * `(lat, lng)` is the coordinate the provider queries at (resolved per-provider
 * by the orchestrator — see `effectiveQueryCoord` in `index.ts`); the cache key
 * follows it, so a centroid-querying provider caches under the centroid's grid
 * cell. `geo` (the full coordinate context — centroid / boundary / address) is
 * passed straight through to `provider.fetch` for providers that need more than
 * a single point.
 */
export async function withCache<T>(
  provider: DataProvider<T>,
  lat: number,
  lng: number,
  apiKey?: string,
  geo?: ProviderGeoContext,
): Promise<ProviderResult<T> & { cached: boolean }> {
  throwIfAborted(geo?.signal);
  const providerKey = provider.cacheKey?.({ lat, lng, geo }) ?? gridCellKey(lat, lng);
  const cacheKey = `${provider.id}:${providerKey}${geo?.studyAreaScope ? `:study-${geo.studyAreaScope.selectionHash}` : ''}${acceptanceCacheSuffix(provider.id)}`;

  // Try reading from Firestore cache. In a server/job context (no
  // authenticated user) the client SDK read is rejected by security
  // rules, so route through the admin SDK there — TASK-57.
  try {
    let cached: Record<string, any> | undefined;
    if (isServerContext()) {
      const adminDb = await getAdminFirestore();
      const snap = await adminDb.collection('cache').doc(cacheKey).get();
      if (snap.exists) cached = snap.data();
    } else {
      const { db } = await import('../../firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const cacheDoc = await getDoc(doc(db, 'cache', cacheKey));
      if (cacheDoc.exists()) cached = cacheDoc.data();
    }

    throwIfAborted(geo?.signal);
    if (cached) {
      const expiresAt = new Date(cached.expiresAt).getTime();
      if (expiresAt > Date.now()) {
        return {
          data: cached.data as T,
          sources: cached.sources.map((s: any) => ({ ...s, source: s.source + ' (cached)' })),
          cached: true,
        };
      }
    }
  } catch (err) {
    rethrowIfAborted(geo?.signal, err);
    // PH-A1.10 — surface what was previously a silent catch. Cache
    // misses on quota / permissions used to fall through silently and
    // show up as "every provider does a live fetch every time" with no
    // diagnostic trail. console.warn makes the failure visible in
    // Cloud Run logs without blocking the live-fetch fallback.
    // eslint-disable-next-line no-console
    console.warn(`[cache:${provider.id}] read failed:`, err);
  }

  // Live fetch — PU5 wraps with transient-failure retry. Permanent
  // failures (misconfigured / permanent-unavailable) throw on the
  // first attempt; transient failures get up to 2 retries (1s + 3s
  // backoff). See `callProviderWithRetry` for the rationale.
  const result = await callProviderWithRetry(provider, lat, lng, apiKey, geo);
  throwIfAborted(geo?.signal);

  // Write to cache (fire-and-forget). A provider may return a result-specific
  // TTL, including 0 to suppress persistence for a degraded/transient fallback.
  //
  // Defensive — Firestore rejects `undefined` field values with
  // `Unsupported field value: undefined`. Individual providers
  // occasionally produce `undefined` for an optional field when the
  // upstream API omits it (e.g. AirNow PM2.5 when the closest station
  // only reports O3). `stripUndefined` walks the payload once and
  // elides those keys, so the cache write never fails on the next
  // provider that has the same shape. (Source providers SHOULD also
  // avoid emitting undefined, but this layer catches the entire class.)
  //
  // TASK-57 — in a server/job context (SED bootstrap, report-gen) there
  // is no authenticated user, so the CLIENT SDK write is denied by
  // Firestore security rules (`PERMISSION_DENIED`) and the cache never
  // populates. Route the write through the ADMIN SDK there, which
  // bypasses rules and writes the SAME `cache/{cacheKey}` doc + shape.
  // In the browser, keep the existing client `setDoc`.
  const requestedTtlMs = result.cacheTtlMs ?? provider.cacheTtlMs;
  const resultTtlMs = Number.isFinite(requestedTtlMs)
    ? Math.max(0, requestedTtlMs)
    : provider.cacheTtlMs;
  try {
    throwIfAborted(geo?.signal);
    if (resultTtlMs === 0) return { ...result, cached: false };
    const payload = stripUndefined<CacheDocPayload>({
      data: result.data,
      sources: result.sources,
      cachedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + resultTtlMs).toISOString(),
    });
    if (isServerContext()) {
      const adminDb = await getAdminFirestore();
      await adminDb.collection('cache').doc(cacheKey).set(payload);
    } else {
      const { db } = await import('../../firebase');
      const { doc, setDoc } = await import('firebase/firestore');
      await setDoc(doc(db, 'cache', cacheKey), payload);
    }
  } catch (err) {
    rethrowIfAborted(geo?.signal, err);
    // PH-A1.10 — surface the silent catch. Write failures (quota,
    // permission, oversized doc) used to be invisible; the next call
    // would refetch from the live API and re-fail to cache forever.
    // Non-fatal so we don't fail the report, but the warn ensures
    // ops can see the cache-write drop in logs.
    // eslint-disable-next-line no-console
    console.warn(`[cache:${provider.id}] write failed:`, err);
  }

  throwIfAborted(geo?.signal);
  return { ...result, cached: false };
}
